import React, { useState, useRef, useEffect } from "react"
import { useTranslation } from "react-i18next"

import {
  styled,
  Typography,
  Popper,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Grow,
  darken
} from "@mui/material"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"

import HKVLink from "../HKVLink"

import { useMatch, useLocation } from "react-router-dom"

/**
 * Het menu krimpt in stappen mee met het scherm: onder 1200 px minder tussenruimte, onder 1100 px
 * geen, en daaronder maakt het mobiele menu het over. Op het item zelf staat `nowrap`: liever krap
 * dan een label over twee regels.
 */
const StyledDesktopMenu = styled("div", {
})(({ theme }) => {
  return {
    display: "flex", alignItems: "center",
    gap: theme.spacing(5),
    minWidth: 0,
    "& .menu-items": {
      display: "flex", alignItems: "center",
      gap: theme.spacing(2),
      minWidth: 0
    },
    [theme.breakpoints.down("lg")]: {
      gap: theme.spacing(2),
      "& .menu-items": {
        gap: theme.spacing(0.85)
      },
    },
    [theme.breakpoints.down(1100)]: {
      "& .menu-items": {
        gap: 0
      }
    },
    [theme.breakpoints.down(1000)]: {
      display: "none"
    }
  }
})

const StyledMenuItem = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "clickable"
})(({ active, clickable = true, theme, color }) => {
  return {
    fontWeight: 500,
    cursor: clickable ? "pointer" : "default",
    whiteSpace: "nowrap",
    padding: theme.spacing(0, 1),
    [theme.breakpoints.down(1100)]: {
      padding: theme.spacing(0, 0.75)
    },
    color: active ? (color ? darken(color, 0.1) : theme.palette.primary.main) : (color ? color : "#212121"),
    "&:hover": clickable ? {
      color: active ? (color ? darken(color, 0.2) : theme.palette.primary.main) : (color ? color : theme.palette.primary.main)
    } : {},
    display: "flex",
    alignItems: "center"
  }
})

const StyledSubMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "active"
})(({ active, theme }) => {
  return {
    fontWeight: 500,
    fontSize: "1rem",
    padding: theme.spacing(1.25, 2.5),
    color: active ? theme.palette.primary.main : "#212121",
    "&:hover": {
      backgroundColor: "transparent",
      color: theme.palette.primary.main
    },
    "&.Mui-focusVisible": {
      backgroundColor: "transparent"
    }
  }
})

const MenuItemWithSubmenu = ({ menuItem, variant, t, currentPath }) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)
  const closeTimerRef = useRef(null)

  useEffect(() => {
    cancelClose()
    setOpen(false)
  }, [currentPath])

  const isActive = menuItem.menuItems?.some(
    (sub) => sub.path && currentPath === `/${t(sub.path)}`
  )

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const handleOpen = () => {
    cancelClose()
    setOpen(true)
  }

  const handleCloseDeferred = () => {
    cancelClose()
    closeTimerRef.current = setTimeout(() => {
      setOpen(false)
    }, 150)
  }

  const handleCloseImmediate = () => {
    cancelClose()
    setOpen(false)
  }

  const handleClickAway = (e) => {
    if (anchorRef.current && anchorRef.current.contains(e.target)) return
    handleCloseImmediate()
  }

  return (
    <div
      ref={anchorRef}
      onMouseEnter={handleOpen}
      onMouseLeave={handleCloseDeferred}
      onClick={handleOpen}
      style={{ display: "inline-flex", alignItems: "center" }}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <StyledMenuItem
          className="transition menu-item"
          active={!!isActive}
          clickable={false}
          variant={variant}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          {t(menuItem.label)}
        </StyledMenuItem>
        <KeyboardArrowDownIcon fontSize="small" />
      </div>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        style={{ zIndex: 4 }}
        modifiers={[
          {
            name: "offset",
            options: { offset: [0, 8] }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: "top left" }}>
            <Paper
              elevation={3}
              onMouseEnter={handleOpen}
              onMouseLeave={handleCloseDeferred}
              sx={{
                minWidth: 220,
                pt: 1,
                pb: 1
              }}
            >
              <ClickAwayListener onClickAway={handleClickAway}>
                <MenuList
                  autoFocusItem={false}
                  sx={{ display: "flex", flexDirection: "column", py: 0 }}
                >
                  {menuItem.menuItems.map((sub, ix) => {
                    const subActive = sub.path && currentPath === `/${t(sub.path)}`
                    return (
                      <HKVLink
                        key={ix}
                        to={`/${t(sub.path)}`}
                        title={t(sub.label)}
                        onClick={handleCloseImmediate}
                        style={{ textDecoration: "none", color: "inherit", display: "block" }}
                      >
                        <StyledSubMenuItem active={!!subActive}>
                          {t(sub.label)}
                        </StyledSubMenuItem>
                      </HKVLink>
                    )
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  )
}

const LeafMenuItem = ({ menuItem, variant, t }) => {
  const match = useMatch(`/${menuItem.path}`)

  return (
    <HKVLink
      to={`/${menuItem.path}`}
      title={t(menuItem.label)}
    >
      <StyledMenuItem
        className="transition menu-item"
        active={!!match}
        variant={variant}
      >
        {t(menuItem.label)}
      </StyledMenuItem>
    </HKVLink>
  )
}

const DesktopMenu = (props) => {
  const { isHome, menuItems } = props
  const { t } = useTranslation()
  const location = useLocation()

  // Overal dezelfde maat, ook op de homepagina: daar stonden de items op `h6` en dat liep uit de
  // pas met de rest van de site.
  const variant = "body1"

  // Sta je al op de homepagina, dan verandert de route niet en doet `ScrollToTop` niets: je klikt
  // op het logo en er gebeurt zichtbaar niets. Dan hoort hij terug naar boven te gaan, met dezelfde
  // vloeiende beweging als de scrollknop rechtsonder.
  const handleLogoClick = () => {
    if (location.pathname !== "/") return
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  return (
    <StyledDesktopMenu>
      <div className="menu-items">
        <HKVLink to="/" title="Home" onClick={handleLogoClick}>
          {/* `alt` hoort op de <img>, niet op de <picture>: daar is het geen geldig attribuut en
              leest een schermlezer het niet. */}
          <picture title="Eggspedition">
            <source media="(max-width: 960px)" srcSet="/static/images/logo-175x124.png" />
            <img
              alt="Eggspedition"
              src={isHome
                ? "/static/images/logo-175x124.png"
                : "/static/images/logo-113x80.png"
              }
            />
          </picture>
        </HKVLink>

        {menuItems.map((menuItem, ix) => {
          if (!menuItem.path && Array.isArray(menuItem.menuItems) && menuItem.menuItems.length > 0) {
            return (
              <MenuItemWithSubmenu
                key={ix}
                menuItem={menuItem}
                variant={variant}
                t={t}
                currentPath={location.pathname}
              />
            )
          }

          if (!menuItem.path) return null

          return (
            <LeafMenuItem
              key={ix}
              menuItem={menuItem}
              variant={variant}
              t={t}
            />
          )
        })}
      </div>
    </StyledDesktopMenu>
  )
}

export default React.memo(DesktopMenu)